import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CloudUpload, Hourglass, Info, X } from 'lucide-react'
import { Button, IconButton } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatNumber, pluralize } from '@/lib/format'
import { useAuthStore } from '@/stores/useAuthStore'
import { useSessionGuard } from '@/stores/useSessionGuard'

/** Tab-scoped on purpose: "for this session" is exactly what sessionStorage means. */
const DISMISS_KEY = 'argus:ephemeral-notice'

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === 'dismissed'
  } catch {
    // Storage is unavailable in some privacy modes. Showing the notice is the
    // safe side of that failure.
    return false
  }
}

function writeDismissed(dismissed: boolean): void {
  try {
    if (dismissed) window.sessionStorage.setItem(DISMISS_KEY, 'dismissed')
    else window.sessionStorage.removeItem(DISMISS_KEY)
  } catch {
    // Nothing to do — the in-memory state still holds for this page view.
  }
}

/** Rounded up, so forty seconds left never reads as "0 minutes". */
function formatRemaining(ms: number): string {
  const minutes = Math.ceil(ms / 60_000)
  if (minutes <= 0) return 'any moment now'
  if (minutes < 60) return `${minutes} ${pluralize(minutes, 'minute')}`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (rest === 0) return `${hours} ${pluralize(hours, 'hour')}`
  return `${hours}h ${rest}m`
}

/**
 * The standing reminder that this instance is a scratchpad.
 *
 * Calm and permanent while there is time left — a user who understands the model
 * from the start does not lose work to it. It escalates to a warning inside the
 * last stretch of the session, when the remaining time is the only thing standing
 * between an hour of review and an empty database.
 */
export default function EphemeralNotice(): ReactElement | null {
  const navigate = useNavigate()
  const authed = useAuthStore((state) => state.user !== null)
  const { expiringSoon, msUntilExpiry, unsavedCount, savePath } = useSessionGuard()
  const [dismissed, setDismissed] = useState(readDismissed)

  // Starts false so a page that loads already inside the warning window counts as
  // a flip, and an earlier dismissal does not hide the escalation.
  const wasExpiringSoon = useRef(false)

  useEffect(() => {
    if (expiringSoon && !wasExpiringSoon.current) {
      setDismissed(false)
      writeDismissed(false)
    }
    wasExpiringSoon.current = expiringSoon
  }, [expiringSoon])

  if (!authed || dismissed) return null

  const dismiss = (): void => {
    setDismissed(true)
    writeDismissed(true)
  }

  const remaining = msUntilExpiry === null ? null : formatRemaining(msUntilExpiry)

  return (
    <div
      className={cn(
        'shrink-0 border-b',
        expiringSoon
          ? 'border-warn-500/40 bg-warn-100/70 dark:bg-warn-950/50'
          : 'border-line bg-sunken/60'
      )}
    >
      {/* Wraps rather than crushes: on a phone the actions drop to a second line
          instead of squeezing the copy into a column of single words. */}
      <div className="mx-auto flex w-full max-w-[90rem] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 sm:px-6 lg:px-8">
        {expiringSoon ? (
          <Hourglass
            className="size-4 shrink-0 text-warn-700 dark:text-warn-300"
            aria-hidden="true"
          />
        ) : (
          <Info className="size-4 shrink-0 text-fg-subtle" aria-hidden="true" />
        )}

        {/* A live region from first paint: initial content is not announced, so the
            only thing a screen reader hears is the escalation replacing the calm
            copy — which is exactly the moment that matters. */}
        <p
          role="status"
          aria-live="polite"
          className="min-w-[14rem] flex-1 text-xs leading-relaxed text-fg-muted"
        >
          {expiringSoon ? (
            <>
              <strong className="font-semibold text-fg">
                This session ends {remaining === null ? 'shortly' : `in ${remaining}`}.
              </strong>{' '}
              {unsavedCount > 0
                ? `${formatNumber(unsavedCount)} ${pluralize(unsavedCount, 'image')} with unsaved alt text will be deleted with it, along with the connected site. Save to WordPress to keep the work.`
                : 'The connected site and everything fetched with it are deleted then. Anything already saved to WordPress stays.'}
            </>
          ) : (
            <>
              Connected sites and generated alt text live only for this session — signing out or
              letting it expire removes them. Saving to WordPress is what makes the work permanent.
            </>
          )}
        </p>

        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {/* Offered even when the count reads zero: it is a floor, and a filtered
              grid can hide unsaved work this session is about to take with it. */}
          {expiringSoon ? (
            <Button
              size="sm"
              variant={unsavedCount > 0 ? 'primary' : 'secondary'}
              iconLeft={<CloudUpload />}
              onClick={() => navigate(savePath)}
            >
              Save to WordPress
            </Button>
          ) : null}

          <IconButton label="Dismiss this notice" variant="ghost" size="sm" onClick={dismiss}>
            <X aria-hidden="true" />
          </IconButton>
        </span>
      </div>
    </div>
  )
}

export { EphemeralNotice }
